<template>
    <div class="campaign-box campaign-box--no-top-border campaign-box--top-padding" data-dusk="select-payment-plan">
        <div class="col-md-12 text-center" v-if="loading">
            <i class="fa fa-spinner fa-spin inner-loading"></i>
        </div>

        <form @submit.prevent="save" data-dusk="ready" v-else>
            <validation-errors :errors="errors"></validation-errors>

            <div class="row">
                <div class="col-sm-4">
                    <h4>Set up your payment plan</h4>
                    <hr>
                    <p class="text-muted"><small><strong>Continuous Traffic Plan</strong></small></p>
                    <p class="text-muted m-b-lg">Advertisers will enjoy uninterrupted service. You will determine an amount you would like to be charged to your credit card (“replenishment amount”) initially, and then whenever your account has been depleted to $0.00, your credit card will be charged the replenishment amount and you will receive an e-mail notification.</p>

                    <p class="text-muted"><small><strong>Pay-As-You-Go Plan</strong></small></p>
                    <p class="text-muted">Need to maintain flexibility throughout the month? In this plan, you pay a minimum of ${{ minimumFundingAmount | currency }} to activate your account. When your balance is depleted to 20% of your original amount, we’ll notify you via e-mail that you need to replenish your account funds. If you exhaust the funds in your account, we suspend your service until you make another deposit to cover additional click activity.</p>
                </div>

                <div class="col-sm-8">
                    <div class="form-group">
                        <label class="control-label">Product Category</label>

                        <select class="form-control" data-dusk="fundsPoolId" v-model="paymentPlan.fundsPoolId">
                            <option :value="fundsPool.id" v-for="fundsPool in nonInvoicedFundsPools">{{ fundsPool.name }}</option>
                        </select>
                    </div>

                    <div class="form-group" v-validation-field:type="errors">
                        <label class="control-label">
                            Plan Option
                        </label>


                        <select class="form-control" data-dusk="paymentPlanType" v-model="paymentPlan.type">
                            <option value="" disabled>Select Plan Type</option>
                            <option :value="paymentPlanType.id" v-for="paymentPlanType in paymentPlanTypes">{{ paymentPlanType.label }}</option>
                        </select>
                    </div>

                    <div v-if="showFundingOptions">
                        <div class="form-group" v-validation-field:fundingSourceId="errors">
                            <label class="control-label">Funding Source</label>

                            <select class="form-control" data-dusk="fundingSourceId" :value="validFundingSourceIdOrEmpty" @change="updateFundingSource">
                                <option disabled value="">Select a Card</option>
                                <option :value="fundingSource.id" v-for="fundingSource in fundingSources">{{ fundingSource.name }}</option>
                            </select>
                        </div>

                        <div class="form-group" v-validation-field:amount="errors">
                            <label class="control-label">Replenish Amount</label>

                            <input class="form-control" type="text" data-dusk="amount" v-money="moneyConfig" v-model="paymentPlan.amount">
                        </div>
                    </div>
                </div>
            </div>

            <hr>

            <div class="pull-right --edit">
                <button class="btn btn-success" :class="{disabled: saving}" data-dusk="save">Save Payment Plan</button>
            </div>
        </form>
    </div>
</template>

<script type="text/babel">
    import ValidationErrors from './../utils/validation-errors.vue';
    import ValidationField from './../../directives/validation-field';
    import Swal from "sweetalert2";

    export default {
        components: { 'validation-errors': ValidationErrors },

        directives: { 'validation-field': ValidationField },

        props: {
            advertiser: Object,
            fundingMinimumsByProductGroup: Object,
            nonInvoicedFundsPools: Array,
            fundingSources: Array,
            nonInvoicedPaymentPlans: Array,
        },

        data() {
            return {
                loading: true,
                saving: false,
                errors: {},
                paymentPlanTypes: [],
                paymentPlan: {},
                fundsPool: {},
                moneyConfig: {
                    decimal: '.',
                    thousands: ',',
                    prefix: '$ ',
                    precision: 0,
                    masked: false /* doesn't work with directive */
                }
            }
        },

        mounted() {
            Promise.all([
                this.getPaymentPlanTypes(),
            ]).then(() => {
                return new Promise(resolve => {
                    const firstPaymentPlan = this.nonInvoicedPaymentPlans[0] || null;

                    if (firstPaymentPlan) {
                        this.refreshForm(firstPaymentPlan.fundsPoolId);
                    }

                    resolve();
                });
            }).then(() => {
                this.loading = false;
            }).catch((error) => {
                new Swal('Whoops!', 'Something went wrong. Please contact support.', 'error');
            });

            this.$watch('paymentPlan.fundsPoolId', value => {
                this.refreshForm(value);
            });
        },

        computed: {
            validFundingSourceIdOrEmpty() {
                const selectedSource = _(this.fundingSources).find({id: Number(this.paymentPlan.fundingSourceId)});

                return selectedSource ? selectedSource.id : '';
            },

            showFundingOptions() {
                let selectedType = _(this.paymentPlanTypes).find({id: this.paymentPlan.type});

                return selectedType && selectedType.label === 'Continuous Traffic';
            },

            minimumFundingAmount() {
                return this.fundingMinimumsByProductGroup[this.fundsPool.productGroupId]
                    || this.fundingMinimumsByProductGroup['default'];
            },
        },

        methods: {
            getPaymentPlanTypes() {
                return this.$http.get(route('billing-setup.payment-plan-types.index'))
                    .then(({ data }) => {
                        this.paymentPlanTypes = data.paymentPlanTypes;
                    });
            },

            updateFundingSource(event) {
                this.paymentPlan.fundingSourceId = Number(event.target.value);
            },

            refreshForm(fundsPoolId) {
                this.paymentPlan = JSON.parse(JSON.stringify(
                    _(this.nonInvoicedPaymentPlans).find({fundsPoolId: fundsPoolId})
                ));

                this.fundsPool = JSON.parse(JSON.stringify(
                    _(this.nonInvoicedFundsPools).find({id: fundsPoolId})
                ));
            },

            save() {
                if (this.saving) {
                    return;
                }

                this.saving = true;

                let payload = {
                    id: this.paymentPlan.id || null,
                    fundsPoolId: this.paymentPlan.fundsPoolId,
                    type: this.paymentPlan.type,
                    amount: this.stripNonNumericCharacters(this.paymentPlan.amount),
                    fundingSourceId: this.paymentPlan.fundingSourceId,
                    productGroupId: this.fundsPool.productGroupId,
                }

                this.$http.put(route('billing-setup.payment-plan.update'), payload)
                    .then(() => {
                        sweetAlert.fire(
                            'Success',
                            'Payment Plan Saved!',
                            'success'
                        ).then(() => {
                            this.$emit('refreshStep');
                        });
                    }).catch(({ status, data }) => {
                        if (status === 422) {
                            this.errors = data.errors;
                        } else if (data.message && data.success === false) {
                            this.errors = {message: [data.message]};
                        } else {
                            new Swal('Whoops!', 'Something went wrong. Please contact support.', 'error');
                        }

                        this.saving = false;
                    });
            },

            stripNonNumericCharacters(amount) {
                return Number(amount.replace(/[^0-9\.-]+/g,""));
            }
        }
    }
</script>
